import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { ICreatorCampaignExport } from '@pages/campaigns/campaigns.interface';
import { catchError, EMPTY, Observable, switchMap, tap } from 'rxjs';
import { CreatorCampaignExportService } from './creator-campaign-export.service';

interface ICreatorCampaignExportState {
  creatorCampaignExport: ICreatorCampaignExport | null;
  loading: boolean;
  error: string | null;
}

const initialState: ICreatorCampaignExportState = {
  creatorCampaignExport: null,
  error: null,
  loading: false,
};

@Injectable()
export class CreatorCampaignExportStore extends ComponentStore<ICreatorCampaignExportState> {
  public readonly creatorCampaignExport$ = this.selectSignal(
    (state: ICreatorCampaignExportState) => state.creatorCampaignExport,
  );
  public readonly loading$ = this.selectSignal((state: ICreatorCampaignExportState) => state.loading);
  public readonly error$ = this.selectSignal((state: ICreatorCampaignExportState) => state.error);

  public readonly createCreatorCampaignExport = this.effect(
    (trigger$: Observable<{ campaignId: string; creatorId: string }>) => {
      return trigger$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ campaignId, creatorId }) =>
          this.creatorCampaignExportService.createCreatorCampaignExport(campaignId, creatorId).pipe(
            tap((creatorCampaignExport: ICreatorCampaignExport) => {
              this.patchState({ creatorCampaignExport, loading: false });
            }),
            catchError(() => {
              this.patchState({
                error: 'Failed to create creator campaign export',
                loading: false,
              });

              return EMPTY;
            }),
          ),
        ),
      );
    },
  );

  public readonly updateCreatorCampaignExport = this.effect(
    (trigger$: Observable<{ id: string; isActive: boolean }>) => {
      return trigger$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ id, isActive }) =>
          this.creatorCampaignExportService.updateCreatorCampaignExport(id, isActive).pipe(
            tap((creatorCampaignExport: ICreatorCampaignExport) => {
              this.patchState({ creatorCampaignExport, loading: false });
            }),
            catchError(() => {
              this.patchState({
                error: 'Failed to update creator campaign export',
                loading: false,
              });

              return EMPTY;
            }),
          ),
        ),
      );
    },
  );

  constructor(private readonly creatorCampaignExportService: CreatorCampaignExportService) {
    super(initialState);
  }
}
